import { createApp } from 'vue'
import { createPinia } from 'pinia'
import i18n from './plugins/i18n'
import App from './App.vue'

import './assets/global.css'; 

import eventBus from './plugins/eventBus';
import { useWebsocketStore } from '@/stores/websocketStore'
import { v4 as uuidv4 } from "uuid";
//import { connectReceiverWebSocket } from './plugins/receiverWebsocket';

//vue serve
//vue build

const app = createApp(App)
const pinia = createPinia()
app.use(i18n)
app.use(pinia)
app.mount('#app')

let mode;
let name;
let workerRoomCode = null;
const pendingCastInitializations = [];
let wakeLock = null;
let wakeLockRequest = null;
let wakeLockUnavailableLogged = false;

async function keepScreenAwake(logLifecycle = () => {}) {
  if (!('wakeLock' in navigator)) {
    if (!wakeLockUnavailableLogged) {
      wakeLockUnavailableLogged = true;
      console.log('Screen Wake Lock API unavailable');
      logLifecycle('wake-lock-unavailable');
    }
    return false;
  }

  if (document.visibilityState !== 'visible') return false;
  if (wakeLock && !wakeLock.released) return true;
  if (wakeLockRequest) return wakeLockRequest;

  wakeLockRequest = navigator.wakeLock.request('screen')
    .then((sentinel) => {
      wakeLock = sentinel;
      console.log('Wake lock acquired');
      logLifecycle('wake-lock-acquired');
      sentinel.addEventListener('release', () => {
        wakeLock = null;
        console.log('Wake lock released');
        logLifecycle('wake-lock-released', {
          visibilityState: document.visibilityState,
        });
      }, { once: true });
      return true;
    })
    .catch((error) => {
      wakeLock = null;
      console.error('Wake lock failed:', error);
      logLifecycle('wake-lock-failed', {
        name: error?.name || null,
        message: error?.message || String(error),
      });
      return false;
    })
    .finally(() => {
      wakeLockRequest = null;
    });

  return wakeLockRequest;
}

let uid = localStorage.getItem('uid');
if (!uid) {
  uid = uuidv4();
  localStorage.setItem('uid', uid);
}

const custom_tag = "Smeegoon"
var castDebugLogger;
let castDebugOverlayEnabled = false;
if (typeof castDebugLogger === 'undefined') {
  castDebugLogger = {
    debug: () => {},
  };
}

// This is used to initialize app when used in chromecast
function initializeCastReceiver() {
  if (typeof window.cast !== 'undefined' && window.cast.framework) {
    const context = window.cast.framework.CastReceiverContext.getInstance();
    console.log('Initializing Google Cast Receiver');

    // Debug Logger
    castDebugLogger = window.cast.debug.CastDebugLogger.getInstance();

    // The admin dashboard controls whether the native Cast diagnostics are
    // visible. Keep logging enabled so it is ready the moment it is requested.
    context.addEventListener(window.cast.framework.system.EventType.READY, () => {
      //castDebugLogger.setEnabled(true);
      //castDebugLogger.showDebugLogs(castDebugOverlayEnabled);
      castDebugLogger.debug(custom_tag, 'User agent');
      castDebugLogger.debug(custom_tag, navigator.userAgent);
    });

    const options = new cast.framework.CastReceiverOptions();
    // Keep the receiver application alive while players are using the Worker
    // transport. Those WebSocket controls are invisible to CAF, so without
    // this option Cast may consider the application inactive.
    options.disableIdleTimeout = true;
    // CAF accepts seconds and does not document an upper bound. Keep this at
    // the largest value that can safely be converted to a JavaScript timer in
    // milliseconds without overflowing the signed 32-bit timeout limit.
    options.maxInactivity = 2147483; // ~24.8 days

    const systemEvents = window.cast.framework.system.EventType;
    const logCastLifecycle = (label, details = {}) => {
      const message = `[Cast lifecycle] ${label} ${JSON.stringify({
        at: new Date().toISOString(),
        senders: context.getSenders?.().length ?? null,
        ...details,
      })}`;
      console.warn(message);
      castDebugLogger.debug(custom_tag, message);
    };

    context.addEventListener(systemEvents.SENDER_CONNECTED, (event) => {
      logCastLifecycle('sender-connected', {
        senderId: event.senderId,
        userAgent: event.userAgent,
      });
    });
    context.addEventListener(systemEvents.SENDER_DISCONNECTED, (event) => {
      logCastLifecycle('sender-disconnected', {
        senderId: event.senderId,
        reason: event.reason,
        userAgent: event.userAgent,
      });
    });
    context.addEventListener(systemEvents.VISIBILITY_CHANGED, (event) => {
      logCastLifecycle('visibility-changed', { isVisible: event.isVisible });
    });
    context.addEventListener(systemEvents.STANDBY_CHANGED, (event) => {
      logCastLifecycle('standby-changed', { isStandby: event.isStandby });
    });
    context.addEventListener(systemEvents.ERROR, (event) => {
      logCastLifecycle('system-error', {
        code: event?.data?.code || event?.code || null,
        reason: event?.data?.reason || event?.reason || null,
      });
    });
    context.addEventListener(systemEvents.SHUTDOWN, () => {
      logCastLifecycle('shutdown');
    });

    document.addEventListener('visibilitychange', () => {
      logCastLifecycle('document-visibility', {
        visibilityState: document.visibilityState,
        hidden: document.hidden,
      });
      if (document.visibilityState === 'visible') {
        keepScreenAwake(logCastLifecycle);
      }
    });
    window.addEventListener('pagehide', (event) => {
      logCastLifecycle('pagehide', { persisted: event.persisted === true });
    });
    window.addEventListener('error', (event) => {
      console.error('[Receiver runtime] uncaught-error', {
        message: event.message || null,
        filename: event.filename || null,
        line: event.lineno || null,
        column: event.colno || null,
        error: event.error?.stack || event.error?.message || null,
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      console.error('[Receiver runtime] unhandled-rejection', {
        reason: reason?.stack || reason?.message || String(reason || 'unknown'),
      });
    });

    // The sender also emits a lightweight Cast heartbeat while its session is
    // connected. Replying on the Cast channel keeps CAF aware that this is an
    // active interactive session even when gameplay travels over WebSockets.
    context.addCustomMessageListener('urn:x-cast:com.smeegoon.ping', (event) => {
      if (!event?.senderId) return;
      context.sendCustomMessage('urn:x-cast:com.smeegoon.ping', event.senderId, {
        event: 'pong',
        data: { receivedAt: Date.now() },
      });
    });

    // The Cast transport tells us which phone launched this receiver.  The
    // Worker owns the actual multiplayer room, so give that sender the
    // internally-created room code as soon as it is available.  The code is
    // never displayed on Chromecast; it is only used by the Flutter app to
    // open its player WebSocket automatically.
    context.addCustomMessageListener('urn:x-cast:com.smeegoon.init', (event) => {
      // CAF passes one CastMessageBus event object, not separate namespace,
      // sender and message arguments.
      const senderId = event?.senderId;
      let data = event?.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          castDebugLogger.debug(custom_tag, 'Ignored malformed Cast init message');
          return;
        }
      }

      if (!senderId || !data || typeof data !== 'object' || !data.userId) {
        castDebugLogger.debug(custom_tag, 'Ignored Cast init without a user id');
        return;
      }

      // Cast receivers are launched by an already-connected sender. Apply
      // its presentation settings immediately instead of waiting for the
      // sender to join the Worker room and for the first room sync to return.
      eventBus.emit('cast-config', {
        userId: data.userId,
        language: data.language,
        theme: data.theme,
        environment: data.environment,
      });

      const initialization = { senderId, data };
      if (workerRoomCode) {
        sendCastRoomReady(context, initialization);
      } else {
        pendingCastInitializations.push(initialization);
        castDebugLogger.debug(custom_tag, 'Cast sender connected; waiting for Worker room');
      }
    });

    context.start(options);
    castDebugLogger.debug(custom_tag, 'Cast Receiver started');
    keepScreenAwake(logCastLifecycle);

    // Chromecast still needs the same Worker-backed room lifecycle as the
    // browser/tablet receiver. Cast supplies the TV transport; the Worker
    // supplies the room code, player membership, and game state.
    initializeGenericReceiver();
  } else {
    castDebugLogger.debug(custom_tag, 'Waiting for Cast framework...');
    setTimeout(initializeCastReceiver, 1000); // Retry after 1000ms
  }
}

eventBus.on('cast-debug-overlay', ({ enabled } = {}) => {
  castDebugOverlayEnabled = Boolean(enabled);
  if (typeof window.cast === 'undefined' || !window.cast.debug) return;
  castDebugLogger = window.cast.debug.CastDebugLogger.getInstance();
  castDebugLogger.setEnabled(true);
  castDebugLogger.showDebugLogs(castDebugOverlayEnabled);
  castDebugLogger.debug(custom_tag, `Diagnostics overlay ${castDebugOverlayEnabled ? 'enabled' : 'disabled'} by admin`);
});

function sendCastRoomReady(context, { senderId, data }) {
  const payload = {
    event: 'cast-room-ready',
    data: {
      roomCode: workerRoomCode,
      userId: data.userId,
      game: data.game || '',
    },
  };
  context.sendCustomMessage('urn:x-cast:com.smeegoon.init', senderId, payload);
  castDebugLogger.debug(custom_tag, `Sent Worker room to Cast sender: ${workerRoomCode}`);
}

function flushPendingCastInitializations() {
  if (mode !== 'cast' || !workerRoomCode || !window.cast?.framework) return;
  const context = window.cast.framework.CastReceiverContext.getInstance();
  while (pendingCastInitializations.length > 0) {
    sendCastRoomReady(context, pendingCastInitializations.shift());
  }
}

// This is used to initialize app when used in generic receiver 
function initializeGenericReceiver(){
  const env = process.env.NODE_ENV
  const roomCodeKey = 'smeegoon-receiver-room-code'
  const storedRoomCode = localStorage.getItem(roomCodeKey)
  name = /^\d{6}$/.test(storedRoomCode || '')
    ? storedRoomCode
    : String(Math.floor(100000 + Math.random() * 900000))
  localStorage.setItem(roomCodeKey, name)
  const width = window.innerWidth
  const height = window.innerHeight
  const websocketStore = useWebsocketStore()
  eventBus.on('receiver-debug', ({ message }) => {
    console.log(`[Receiver] ${message}`)
    castDebugLogger.debug(custom_tag, message)
  })
  if (mode === 'cast') {
    // The Worker room code is an internal identifier for a Chromecast. Its
    // sender is already connected through Google Cast discovery, so the TV
    // proceeds directly to its idle screen instead of advertising a code.
    eventBus.on('name', ({ name: roomCode }) => {
      workerRoomCode = roomCode
      flushPendingCastInitializations()
      eventBus.emit('cast-room-ready', { roomCode })
    })
  }
  websocketStore.connect({ uid, name, width, height, env })

  eventBus.on('receiver-init', onInitReceived)
  eventBus.on('receiver-join', onJoinReceived)
  eventBus.on('receiver-leave', onLeaveReceived)

  eventBus.on('receiver-launch', onLaunchReceived)

  eventBus.on('receiver-idle', onIdleReceived)

  eventBus.on('receiver-start', onStartReceived)
  eventBus.on('receiver-pause', onPauseReceived)
  eventBus.on('receiver-command', onControlReceived)
  eventBus.on('control', onControlReceived)
  eventBus.on('receiver-resume', onResumeReceived)
  eventBus.on('receiver-reset', onResetReceived)
  eventBus.on('receiver-report', onReportReceived)
}

let receiverInitialized = false;
let receiverLaunched = false;

function onInitReceived(event) {
  console.log('Received initialize:', event.data);
  if (event.data.action !== 'init') return;
  if (receiverInitialized) {
    console.log('receiver already initialized')
    return;
  }
  const parameters = event.data.parameters;
  const player = event.data.player
  const game = event.data.game
  console.log('Init config:', { parameters, player, game });
  receiverInitialized = true;
  eventBus.emit('init', event.data);
}

function onLaunchReceived(event){
  console.log('Received launch:', event.data);
  if (event.data.action !== 'launch') return;
  // if receiver has launched
  if (receiverLaunched) {
    console.log(' receiver has already launched game')
    return;
  }
  const { userId, game, room } = event.data;
  receiverLaunched = true
  console.log('Launch config:', {userId, game, room});
  eventBus.emit('launch', event.data);
}

function onIdleReceived(event){
  console.log('Received idle:', event.data);
  if (event.data.action !== 'idle') return;
  receiverLaunched = false;
  eventBus.emit('idle', event.data);
}

function onJoinReceived(event) {
  console.log('Received join:', event.data);
  if (event.data.action !== 'join') return;
  const { userId, gameId, environment } = event.data;
  eventBus.emit('join', event.data);
}

function onLeaveReceived(event) {
  console.log('Received leave:', event.data);
  if (event.data.action !== 'leave') return;
  const { userId, gameId, environment } = event.data;
  eventBus.emit('left', event.data);
}

function onStartReceived(event) {
  console.log('Received start:', event.data);
  //castDebugLogger.debug(custom_tag, 'Received start');
  if (event.data.action !== 'start') return;
  const { game, player } = event.data;
  console.log('Starting game:', game);
  eventBus.emit('start', event.data);
}

function onPauseReceived(event) {
  console.log('Received pause:', event.data);
  if (event.data.action !== 'pause') return; // <- Was incorrectly 'start'
  const { game, player } = event.data;
  console.log('Pausing game:', game);
  eventBus.emit('pause', event.data);
}

function onResumeReceived(event) {
  console.log('Received resume:', event.data);
  if (event.data.action !== 'resume') return;
  const { game, player } = event.data;
  console.log('resuming game:', game);
  eventBus.emit('resume', event.data);
}

function onControlReceived(event) {
  const control = event?.data || event || {}
  console.log('Received control:', control);
  if (control.action === 'ping') {
    castDebugLogger.debug(custom_tag, 'Received ping');
    const sender = event.senderId;
    context.sendCustomMessage('urn:x-cast:com.smeegoon.control', sender, { action: 'pong' });
    console.log('Pong sent');
  }

  if (control.action === 'updateColor') {
    const color = control.color;
    eventBus.emit('cast-message', control);

    console.log(`Update color to: ${color}`);
    // You can then update your UI or trigger the desired action here
  }

  eventBus.emit('game-control', control)
}

function onResetReceived(event) {
  window.location.reload()
}

function onReportReceived(event) {
  //TODO check if room / receiver status matches
}

function sendMessage(senderId, message) {
  const json = typeof message === 'string' ? message : JSON.stringify(message);
  // Chromecast
  if (mode==='cast') {
    console.log('sending message to chromecast client')
    const context = window.cast.framework.CastReceiverContext.getInstance();
    context.sendCustomMessage('urn:x-cast:com.smeegoon.init', senderId, message);
    castDebugLogger.debug(custom_tag, '[Chromecast] Sent to sender:', message);
    return;
  }
  // WebSocket
  if (mode==='web') {
    console.log('sending message to web client')
    //window.webReceiverSocket.send(json);
    console.log('[WebSocket] Sent to sender:', senderId);
    console.log('[WebSocket] Data sent to sender:', json);
    return;
  }

  console.warn('[sendMessageToSender] No available transport to send message.');
}

// Google Cast devices commonly identify themselves with `CrKey`; some
// Android TV receivers expose `AndroidTV`. Keep this detection identical to
// App.vue so transport and UI can never disagree about the receiver mode.
if (/AndroidTV|CrKey/i.test(navigator.userAgent)) {
  console.log('initializing chromecast')
  mode = "cast"
  initializeCastReceiver();
} else {
  console.log('initializing web')
  mode = "web"
  initializeGenericReceiver();
}

// Debug Keyboard Shortcuts
/*window.addEventListener('keydown', (event) => {
  const key = event.key;
  console.log(`Key pressed: ${key}`);

  // Mapping numbers 0-9
  if (key >= '0' && key <= '9') {
    eventBus.emit('debug-key', { type: 'number', value: parseInt(key) });
  }

  // Mapping Arrows, Enter, Backspace
  switch (key) {
    case 'ArrowUp':
      eventBus.emit('debug-key', { type: 'arrow', value: 'up' });
      break;
    case 'ArrowDown':
      eventBus.emit('debug-key', { type: 'arrow', value: 'down' });
      break;
    case 'ArrowLeft':
      eventBus.emit('debug-key', { type: 'arrow', value: 'left' });
      break;
    case 'ArrowRight':
      eventBus.emit('debug-key', { type: 'arrow', value: 'right' });
      break;
    case 'Enter':
      eventBus.emit('debug-key', { type: 'action', value: 'enter' });
      break;
    case 'Backspace':
      eventBus.emit('debug-key', { type: 'action', value: 'back' });
      break;
    case 'p':
      eventBus.emit('debug-key', { type: 'action', value: 'pause' });
      break;
    case 'r':
    case 'R':
      console.log('Manual reset triggered via R key');
      window.location.reload();
      break;
  }
}); */



